/**
 * Member Sidebar Controller
 * Handles multi-state sidebar, form submissions, and garage sync
 */
(function () {
	"use strict";

	const SELECTORS = {
		sidebar: "[data-member-sidebar]",
		overlay: "[data-sidebar-overlay]",
		close: "[data-sidebar-close]",
		showState: "[data-show-state]",
		states: "[data-state]",
		loginForm: "[data-login-form]",
		registerForm: "[data-register-form]",
		recoverForm: "[data-recover-form]",
		formMessages: "[data-form-messages]",
		garageList: "[data-member-garage-list]",
		recentlyViewed: "[data-recently-viewed]",
		openGaragePopup: "[data-open-garage-popup]",
		showRecover: "[data-show-recover]",
		backToLogin: "[data-back-to-login]",
		recoverContainer: "[data-recover-container]",
		recoverySubmit: "[data-recovery-submit]",
		recoveryCooldownMessage: "[data-recovery-cooldown-message]",
	};

	const CLASSES = {
		open: "is-open",
		active: "is-active",
		loading: "is-loading",
		hasMessage: "has-message",
		error: "is-error",
		success: "is-success",
	};

	const STORAGE_KEYS = {
		searchTerms: "searchTerms",
		recentlyViewed: "partify_recently_viewed",
		registrationAttempt: "partify:member-registration-attempt",
		recoveryAttempt: "partify:member-recovery-attempt",
		recoveryCooldownUntil: "partify:member-recovery-cooldown-until",
	};
	const RECOVERY_ATTEMPT_TTL_MS = 5 * 60 * 1000;
	const RECOVERY_COOLDOWN_MS = 60 * 1000;

	let sidebar = null;
	let isCustomerLoggedIn = false;
	let recoveryCooldownTimer = null;

	/**
	 * Initialize the member sidebar
	 */
	function init() {
		/* Idempotency guard — prevent double-init from DOMContentLoaded + readyState check */
		if (window.__PARTIFY_MEMBER_SIDEBAR_BOUND) return;
		window.__PARTIFY_MEMBER_SIDEBAR_BOUND = true;

		console.log("MemberSidebar init() called");
		sidebar = document.querySelector(SELECTORS.sidebar);
		console.log("Sidebar element found:", sidebar);
		if (!sidebar) {
			console.error("Sidebar not found in DOM");
			return;
		}

		// Check if customer is logged in
		isCustomerLoggedIn =
			sidebar.querySelector('[data-state="member"]') !== null;

		// Clear any stale form states if user is logged in
		if (isCustomerLoggedIn) {
			clearAllFormStates();
		}

		bindEvents();
		revealRegistrationErrors();
		revealRecoveryResult();
		applyRecoveryCooldown();

		if (isCustomerLoggedIn) {
			initMemberFeatures();
		}

		// Expose open/close methods globally
		window.MemberSidebar = {
			open: openSidebar,
			close: closeSidebar,
			toggle: toggleSidebar,
			showState: showState,
			refreshGarage: () => {
				if (isCustomerLoggedIn) {
					renderGarageVehicles();
				}
			},
		};
	}

	/**
	 * Bind all event listeners
	 */
	function bindEvents() {
		// Open sidebar triggers
		const openBtns = document.querySelectorAll("[data-open-member-sidebar]");
		console.log("Found", openBtns.length, "open-member-sidebar buttons");
		openBtns.forEach((btn, index) => {
			console.log(`Binding button ${index}:`, btn);
			btn.addEventListener("click", (e) => {
				console.log("Button clicked!", e);
				e.preventDefault();
				openSidebar(e);
			});
		});

		// Close triggers
		const overlay = sidebar.querySelector(SELECTORS.overlay);
		const closeBtn = sidebar.querySelector(SELECTORS.close);

		if (overlay) overlay.addEventListener("click", closeSidebar);
		if (closeBtn) closeBtn.addEventListener("click", closeSidebar);

		// State navigation buttons (hub, signin, signup)
		sidebar.querySelectorAll(SELECTORS.showState).forEach((btn) => {
			btn.addEventListener("click", () => {
				const targetState = btn.getAttribute("data-show-state");
				showState(targetState);
			});
		});

		// Forgot password toggle
		const showRecoverBtns = sidebar.querySelectorAll(SELECTORS.showRecover);
		const backToLoginBtn = sidebar.querySelector(SELECTORS.backToLogin);
		const recoverContainer = sidebar.querySelector(SELECTORS.recoverContainer);
		const loginForm = sidebar.querySelector(SELECTORS.loginForm);

		showRecoverBtns.forEach((btn) => {
			btn.addEventListener("click", () => openRecovery());
		});

		if (backToLoginBtn && recoverContainer && loginForm) {
			backToLoginBtn.addEventListener("click", () => {
				recoverContainer.style.display = "none";
				loginForm.style.display = "flex";
			});
		}

		// Keep native Shopify auth submissions pointed at the page where auth started.
		const loginFormEl = sidebar.querySelector(SELECTORS.loginForm);
		const registerForm = sidebar.querySelector(SELECTORS.registerForm);
		const recoverForm = sidebar.querySelector(SELECTORS.recoverForm);
		const resolvedReturnUrl = resolveAuthReturnUrl();

		setFormReturnUrl(registerForm, resolvedReturnUrl);

		if (loginFormEl) {
			loginFormEl.addEventListener("submit", () => {
				const submitBtn = loginFormEl.querySelector(".member-sidebar__submit");
				if (submitBtn) submitBtn.classList.add(CLASSES.loading);
				clearFormMessages();
			});
		}

		if (registerForm) {
			registerForm.addEventListener("submit", () => {
				const submitBtn = registerForm.querySelector(
					".member-sidebar__submit",
				);
				markRegistrationAttempt();
				setFormReturnUrl(registerForm, resolveAuthReturnUrl());
				if (submitBtn) submitBtn.classList.add(CLASSES.loading);
				clearFormMessages();
			});
		}

		if (recoverForm) {
			recoverForm.addEventListener("submit", () => {
				const submitBtn = recoverForm.querySelector(
					".member-sidebar__submit",
				);
				markRecoveryAttempt();
				if (submitBtn) submitBtn.classList.add(CLASSES.loading);
				clearFormMessages();
			});
		}

		// Open garage popup from sidebar
		sidebar.querySelectorAll(SELECTORS.openGaragePopup).forEach((btn) => {
			btn.addEventListener("click", () => {
				closeSidebar();
				setTimeout(() => {
					if (typeof window.toggleGaragePopup === "function") {
						window.toggleGaragePopup();
					}
				}, 350);
			});
		});

		// Escape key to close
		document.addEventListener("keydown", (e) => {
			if (e.key === "Escape" && sidebar.classList.contains(CLASSES.open)) {
				closeSidebar();
			}
		});

		// Listen for garage updates
		window.addEventListener("storage", (e) => {
			if (e.key === STORAGE_KEYS.searchTerms && isCustomerLoggedIn) {
				renderGarageVehicles();
			}
		});

		// Custom event for garage updates
		window.addEventListener("garageUpdated", () => {
			if (isCustomerLoggedIn) {
				renderGarageVehicles();
			}
		});

		// Secondary event for garage updates
		window.addEventListener("searchTermsUpdated", () => {
			if (isCustomerLoggedIn) {
				renderGarageVehicles();
			}
		});

		// Polling fallback to detect same-tab changes
		// Use a longer interval (2s) and clear previous to prevent stacking.
		if (window.__partifyMemberSidebarPollId) {
			clearInterval(window.__partifyMemberSidebarPollId);
		}
		let lastSearchTermsValue = localStorage.getItem(STORAGE_KEYS.searchTerms);
		window.__partifyMemberSidebarPollId = setInterval(() => {
			const currentValue = localStorage.getItem(STORAGE_KEYS.searchTerms);
			if (currentValue !== lastSearchTermsValue) {
				lastSearchTermsValue = currentValue;
				if (isCustomerLoggedIn) {
					renderGarageVehicles();
				}
			}
		}, 2000);
	}

	/**
	 * Initialize member-specific features
	 */
	function initMemberFeatures() {
		renderGarageVehicles();
		renderRecentlyViewed();
	}

	/**
	 * Show a specific state (hub, signin, signup)
	 */
	function showState(stateName, preserveMessages = false) {
		// Update all states
		sidebar.querySelectorAll(SELECTORS.states).forEach((state) => {
			state.classList.remove(CLASSES.active);
			if (state.getAttribute("data-state") === stateName) {
				state.classList.add(CLASSES.active);
			}
		});

		// Clear form messages when switching states
		if (!preserveMessages) clearFormMessages();

		// Reset recover form visibility when switching away
		const recoverContainer = sidebar.querySelector(SELECTORS.recoverContainer);
		const loginForm = sidebar.querySelector(SELECTORS.loginForm);
		if (recoverContainer && loginForm) {
			recoverContainer.style.display = "none";
			loginForm.style.display = "flex";
		}
	}

	function openRecovery(preserveMessages = false) {
		showState("signin", preserveMessages);

		const recoverContainer = sidebar.querySelector(SELECTORS.recoverContainer);
		const loginForm = sidebar.querySelector(SELECTORS.loginForm);
		if (!recoverContainer || !loginForm) return;

		loginForm.style.display = "none";
		recoverContainer.style.display = "block";
		const recoveryEmail = recoverContainer.querySelector('input[type="email"]');
		if (recoveryEmail) recoveryEmail.focus();
	}

	function markRecoveryAttempt() {
		try {
			sessionStorage.setItem(STORAGE_KEYS.recoveryAttempt, String(Date.now()));
		} catch (error) {
			console.warn("Unable to mark recovery attempt", error);
		}
	}

	function consumeRecoveryAttempt() {
		let attemptedAt = 0;
		try {
			attemptedAt = Number(
				sessionStorage.getItem(STORAGE_KEYS.recoveryAttempt),
			);
			sessionStorage.removeItem(STORAGE_KEYS.recoveryAttempt);
		} catch (error) {
			console.warn("Unable to read recovery attempt", error);
			return false;
		}

		const age = Date.now() - attemptedAt;
		return (
			Number.isFinite(attemptedAt) &&
			age >= 0 &&
			age <= RECOVERY_ATTEMPT_TTL_MS
		);
	}

	function startRecoveryCooldown() {
		try {
			sessionStorage.setItem(
				STORAGE_KEYS.recoveryCooldownUntil,
				String(Date.now() + RECOVERY_COOLDOWN_MS),
			);
		} catch (error) {
			console.warn("Unable to start recovery cooldown", error);
		}
	}

	function clearRecoveryCooldown() {
		try {
			sessionStorage.removeItem(STORAGE_KEYS.recoveryCooldownUntil);
		} catch (error) {
			console.warn("Unable to clear recovery cooldown", error);
		}
	}

	function clearRecoveryCooldownTimer() {
		if (recoveryCooldownTimer === null) return;
		clearTimeout(recoveryCooldownTimer);
		recoveryCooldownTimer = null;
	}

	function renderRecoveryCooldown(submitBtn, cooldownMessage, cooldownUntil) {
		clearRecoveryCooldownTimer();

		const remainingMilliseconds = cooldownUntil - Date.now();
		const remainingSeconds = Math.ceil(remainingMilliseconds / 1000);
		const isActive =
			Number.isFinite(cooldownUntil) &&
			remainingMilliseconds > 0 &&
			remainingMilliseconds <= RECOVERY_COOLDOWN_MS;

		submitBtn.disabled = isActive;
		if (!isActive) {
			if (cooldownMessage) cooldownMessage.hidden = true;
			clearRecoveryCooldown();
			return;
		}

		if (cooldownMessage) {
			const template =
				remainingSeconds === 1
					? cooldownMessage.dataset.cooldownSingular
					: cooldownMessage.dataset.cooldownPlural;
			if (template) {
				cooldownMessage.textContent = template.replace(
					"__SECONDS__",
					String(remainingSeconds),
				);
			}
			cooldownMessage.hidden = false;
		}

		const nextTickIn =
			remainingMilliseconds - (remainingSeconds - 1) * 1000;
		recoveryCooldownTimer = setTimeout(() => {
			renderRecoveryCooldown(submitBtn, cooldownMessage, cooldownUntil);
		}, Math.max(1, nextTickIn));
	}

	function applyRecoveryCooldown() {
		clearRecoveryCooldownTimer();
		const submitBtn = sidebar.querySelector(SELECTORS.recoverySubmit);
		const cooldownMessage = sidebar.querySelector(
			SELECTORS.recoveryCooldownMessage,
		);
		if (!submitBtn) return;

		let cooldownUntil = 0;
		try {
			cooldownUntil = Number(
				sessionStorage.getItem(STORAGE_KEYS.recoveryCooldownUntil),
			);
		} catch (error) {
			console.warn("Unable to read recovery cooldown", error);
		}

		renderRecoveryCooldown(submitBtn, cooldownMessage, cooldownUntil);
	}

	function revealRecoveryResult() {
		const hasDrawerAttempt = consumeRecoveryAttempt();
		if (!hasDrawerAttempt) return;

		const successMarker = sidebar.querySelector(
			"[data-drawer-recovery-success]",
		);
		const errorMarker = sidebar.querySelector("[data-drawer-recovery-errors]");
		const resultMarker = successMarker || errorMarker;
		if (!resultMarker) return;

		openRecovery(true);
		if (successMarker) {
			const recoveryEmail = sidebar.querySelector(
				`${SELECTORS.recoverContainer} input[type="email"]`,
			);
			if (recoveryEmail) recoveryEmail.value = "";
			startRecoveryCooldown();
		}

		sidebar.classList.add(CLASSES.open);
		sidebar.setAttribute("aria-hidden", "false");
		document.body.classList.add("member-sidebar-open");
		document.body.style.overflow = "hidden";
		resultMarker.focus();
	}

	function markRegistrationAttempt() {
		try {
			sessionStorage.setItem(STORAGE_KEYS.registrationAttempt, "pending");
		} catch (error) {
			console.warn("Unable to mark registration attempt", error);
		}
	}

	function consumeRegistrationAttempt() {
		try {
			const isPending =
				sessionStorage.getItem(STORAGE_KEYS.registrationAttempt) === "pending";
			sessionStorage.removeItem(STORAGE_KEYS.registrationAttempt);
			return isPending;
		} catch (error) {
			console.warn("Unable to read registration attempt", error);
			return false;
		}
	}

	function revealRegistrationErrors() {
		const isRegistrationAttempt = consumeRegistrationAttempt();
		if (!isRegistrationAttempt) return;

		const messagesEl = sidebar.querySelector("[data-registration-errors]");
		if (!messagesEl) return;

		showState("signup", true);
		sidebar.classList.add(CLASSES.open);
		sidebar.setAttribute("aria-hidden", "false");
		document.body.classList.add("member-sidebar-open");
		document.body.style.overflow = "hidden";
		messagesEl.focus();
	}

	// Alias for backward compatibility
	function switchTab(tabName) {
		showState(tabName);
	}

	/**
	 * Open the sidebar
	 */
	function getMemberSidebarTrigger(event) {
		if (!event || event.type !== "click" || event.isTrusted === false) {
			return null;
		}

		return (
			event.target &&
			event.target.closest &&
			event.target.closest("[data-open-member-sidebar]")
		);
	}

	function getExplicitHeaderAccountTrigger(event) {
		const trigger = getMemberSidebarTrigger(event);

		if (!trigger) return null;

		const isHeaderAccountTrigger = Boolean(
			trigger.matches(
				".site-header_account-link-anchor, .site-header-mobile-account__btn",
			) ||
				trigger.closest(
					".site-header-actions__account-link, .site-header-mobile-account",
				),
		);

		return isHeaderAccountTrigger ? trigger : null;
	}

	function getExplicitWishlistAuthTrigger(event) {
		const trigger = getMemberSidebarTrigger(event);

		return trigger && trigger.matches("[data-wishlist-auth-trigger]")
			? trigger
			: null;
	}

	function isGarageFlowOpen() {
		const garageV2Overlay = document.querySelector("[data-gv2-overlay]");
		const legacyGaragePopup = document.querySelector(".garage-easyYMM-popup");

		return Boolean(
			document.body.classList.contains("gv2-locked") ||
				document.body.classList.contains("no-scroll-garage-popup") ||
				(garageV2Overlay && !garageV2Overlay.classList.contains("hidden")) ||
				(legacyGaragePopup && !legacyGaragePopup.classList.contains("hidden")),
		);
	}

	function shouldOpenSidebar(event) {
		return Boolean(
			(getExplicitHeaderAccountTrigger(event) ||
				getExplicitWishlistAuthTrigger(event)) &&
				!isGarageFlowOpen(),
		);
	}

	function openSidebar(event) {
		if (!shouldOpenSidebar(event)) return false;

		sidebar.classList.add(CLASSES.open);
		sidebar.setAttribute("aria-hidden", "false");
		document.body.classList.add("member-sidebar-open");
		document.body.style.overflow = "hidden";
		if (isCustomerLoggedIn) {
			try {
				renderGarageVehicles();
			} catch (e) {}
		}

		// Focus trap
		const firstFocusable = sidebar.querySelector("button, input, a");
		if (firstFocusable) firstFocusable.focus();

		return true;
	}

	/**
	 * Close the sidebar
	 */
	function closeSidebar() {
		sidebar.classList.remove(CLASSES.open);
		sidebar.setAttribute("aria-hidden", "true");
		document.body.classList.remove("member-sidebar-open");
		document.body.style.overflow = "";
	}

	/**
	 * Toggle sidebar
	 */
	function toggleSidebar(event) {
		if (sidebar.classList.contains(CLASSES.open)) {
			closeSidebar();
		} else {
			openSidebar(event);
		}
	}

	function resolveAuthReturnUrl() {
		const path = window.location.pathname || "/";
		const search = window.location.search || "";
		const isWishlistUi = Boolean(
			document.querySelector("[data-wishlist-page]"),
		);

		if (isWishlistUi) {
			return "/search?view=wishlist-page&type=product&q=*";
		}

		return `${path}${search}` || "/";
	}

	function setFormReturnUrl(form, returnUrl) {
		if (!form) return;
		let input = form.querySelector('input[name="return_to"]');
		if (!input) {
			input = document.createElement("input");
			input.type = "hidden";
			input.name = "return_to";
			form.appendChild(input);
		}
		input.value = returnUrl || "/";
	}

	/**
	 * Show form message
	 */
	function showMessage(el, message, type) {
		if (!el) return;

		el.textContent = message;
		el.classList.add(CLASSES.hasMessage);
		el.classList.remove(CLASSES.error, CLASSES.success);
		el.classList.add(type === "error" ? CLASSES.error : CLASSES.success);
	}

	/**
	 * Clear all form messages and reset form states
	 */
	function clearAllFormStates() {
		if (!sidebar) return;

		// Clear all error/success messages
		sidebar.querySelectorAll(SELECTORS.formMessages).forEach((el) => {
			el.textContent = "";
			el.classList.remove(CLASSES.hasMessage, CLASSES.error, CLASSES.success);
		});

		// Remove loading states from buttons
		sidebar.querySelectorAll(".member-sidebar__submit").forEach((btn) => {
			btn.classList.remove(CLASSES.loading);
		});

		// Reset all form inputs
		sidebar.querySelectorAll("form").forEach((form) => {
			form.reset();
		});
	}

	/**
	 * Clear all form messages
	 */
	function clearFormMessages() {
		sidebar.querySelectorAll(SELECTORS.formMessages).forEach((el) => {
			el.textContent = "";
			el.classList.remove(CLASSES.hasMessage, CLASSES.error, CLASSES.success);
		});
	}

	/**
	 * Render garage vehicles in sidebar
	 */
	function renderGarageVehicles() {
		const container = sidebar.querySelector(SELECTORS.garageList);
		if (!container) return;

		const vehicles = getGarageVehicles();

		if (vehicles.length === 0) {
			container.innerHTML = `
        <div class="member-sidebar__garage-empty">
          <p style="font-size: 14px; color: #6b7280; text-align: center; padding: 16px;">
            No vehicles in your garage yet.
          </p>
        </div>
      `;
			return;
		}

		container.innerHTML = vehicles
			.map((vehicle, index) => {
				const ymm =
					vehicle.ymm ||
					`${vehicle.year || ""} ${vehicle.make || ""} ${vehicle.model || ""}`.trim();
				const isActive = index === 0;
				const vin = vehicle.vin ? String(vehicle.vin) : "";
				const paintCode = vehicle.paintCode ? String(vehicle.paintCode) : "";

				return `
        <div class="member-sidebar__garage-item ${isActive ? "is-active" : ""}" data-vehicle-index="${index}">
          <div class="member-sidebar__garage-icon">
								<img
									src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI1NiIgaGVpZ2h0PSI1NiIgZmlsbD0iIzAwMDAwMCIgdmlld0JveD0iMCAwIDI1NiAyNTYiPjxwYXRoIGQ9Ik0yNDAsMTkyaC04Vjk4LjY3YTE2LDE2LDAsMCwwLTcuMTItMTMuMzFsLTg4LTU4LjY3YTE2LDE2LDAsMCwwLTE3Ljc1LDBsLTg4LDU4LjY3QTE2LDE2LDAsMCwwLDI0LDk4LjY3VjE5MkgxNmE4LDgsMCwwLDAsMCwxNkgyNDBhOCw4LDAsMCwwLDAtMTZaTTQwLDk4LjY3LDEyOCw0MGw4OCw1OC42NlYxOTJIMTkyVjEzNmE4LDgsMCwwLDAtOC04SDcyYTgsOCwwLDAsMC04LDh2NTZINDBaTTE3NiwxNDR2MTZIMTM2VjE0NFptLTU2LDE2SDgwVjE0NGg0MFpNODAsMTc2aDQwdjE2SDgwWm01NiwwaDQwdjE2SDEzNloiPjwvcGF0aD48L3N2Zz4="
									alt=""
									width="20"
									height="20"
									aria-hidden="true"
								>
          </div>
          <div class="member-sidebar__garage-info">
            <div class="member-sidebar__garage-ymm">${escapeHtml(ymm)}</div>
            ${vin ? `<div class="member-sidebar__garage-meta">VIN: ${escapeHtml(vin)}</div>` : ""}
            ${paintCode ? `<div class="member-sidebar__garage-meta">Paint: ${escapeHtml(paintCode)}</div>` : ""}
          </div>
          <button 
            type="button" 
            class="member-sidebar__garage-edit" 
            data-edit-vehicle="${index}"
            aria-label="Edit vehicle"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 20h9"/>
              <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z"/>
            </svg>
          </button>
          <button 
            type="button" 
            class="member-sidebar__garage-delete" 
            data-delete-vehicle="${index}"
            aria-label="Remove vehicle"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
            </svg>
          </button>
        </div>
      `;
			})
			.join("");

		// Bind delete handlers
		container.querySelectorAll("[data-delete-vehicle]").forEach((btn) => {
			btn.addEventListener("click", (e) => {
				e.stopPropagation();
				const index = parseInt(btn.getAttribute("data-delete-vehicle"), 10);
				deleteVehicle(index);
			});
		});

		// Bind edit handlers
		container.querySelectorAll("[data-edit-vehicle]").forEach((btn) => {
			btn.addEventListener("click", (e) => {
				e.stopPropagation();
				const index = parseInt(btn.getAttribute("data-edit-vehicle"), 10);
				const vehicle = vehicles[index];
				if (vehicle) {
					closeSidebar();
					openGarageEdit(vehicle);
				}
			});
		});

		// Bind click to select vehicle
		container
			.querySelectorAll(".member-sidebar__garage-item")
			.forEach((item) => {
				item.addEventListener("click", () => {
					const index = parseInt(item.getAttribute("data-vehicle-index"), 10);
					selectVehicle(index);
				});
			});
	}

	/**
	 * Delete vehicle from garage
	 */
	function deleteVehicle(index) {
		try {
			let vehicles =
				JSON.parse(localStorage.getItem(STORAGE_KEYS.searchTerms)) || [];
			vehicles.splice(index, 1);
			persistGarageVehicles(vehicles, 16);

			renderGarageVehicles();

			// Trigger global refresh
			window.dispatchEvent(
				new CustomEvent("garageUpdated", { detail: vehicles }),
			);
			window.dispatchEvent(
				new CustomEvent("partify:vehicle:changed", {
					detail: vehicles[0] || null,
				}),
			);
		} catch (e) {
			console.error("Error deleting vehicle:", e);
		}
	}

	/**
	 * Select a vehicle (move to front)
	 */
	function selectVehicle(index) {
		try {
			let vehicles =
				JSON.parse(localStorage.getItem(STORAGE_KEYS.searchTerms)) || [];
			if (index === 0 || index >= vehicles.length) return;

			const [selected] = vehicles.splice(index, 1);
			vehicles.unshift(selected);
			persistGarageVehicles(vehicles, 17);

			renderGarageVehicles();

			// Navigate to collection
			const handle = generateCollectionHandle(selected.ymm);
			if (handle) {
				closeSidebar();
				window.location.href = `/collections/${handle}`;
			}
		} catch (e) {
			console.error("Error selecting vehicle:", e);
		}
	}

	/**
	 * Generate collection handle from YMM
	 */
	function generateCollectionHandle(ymm) {
		if (!ymm) return "";
		return ymm
			.toLowerCase()
			.trim()
			.replace(/\s+/g, "-")
			.replace(/[^a-z0-9-]/g, "-")
			.replace(/-+/g, "-");
	}

	function getGarageVehicles() {
		try {
			const raw = localStorage.getItem(STORAGE_KEYS.searchTerms);
			const list = raw ? JSON.parse(raw) : [];
			if (!Array.isArray(list)) return [];

			let changed = false;
			const normalized = list.map((vehicle) => {
				if (!vehicle || typeof vehicle !== "object") return vehicle;
				if (!Object.prototype.hasOwnProperty.call(vehicle, "vin")) {
					vehicle.vin = vehicle.vin || null;
					changed = true;
				}
				if (!Object.prototype.hasOwnProperty.call(vehicle, "paintCode")) {
					vehicle.paintCode = vehicle.paintCode || null;
					changed = true;
				}
				return vehicle;
			});

			if (changed) {
				localStorage.setItem(
					STORAGE_KEYS.searchTerms,
					JSON.stringify(normalized),
				);
			}

			return normalized;
		} catch (e) {
			return [];
		}
	}

	function openGarageEdit(vehicle) {
		if (!vehicle) return;

		const ymm =
			vehicle.ymm ||
			`${vehicle.year || ""} ${vehicle.make || ""} ${vehicle.model || ""}`.trim();
		const collectionHandle = generateCollectionHandle(ymm);

		try {
			window.editCarState = {
				open: true,
				ymm: ymm,
				url: `/collections/${collectionHandle}`,
			};
		} catch (e) {
			// no-op
		}

		if (typeof window.toggleGaragePopup === "function") {
			window.toggleGaragePopup();
		}

		setTimeout(() => {
			try {
				if (
					window.GarageLogic &&
					typeof window.GarageLogic.updateGaragePopup === "function"
				) {
					window.GarageLogic.updateGaragePopup(getGarageVehicles());
				}
			} catch (e) {
				// no-op
			}
		}, 350);
	}

	/**
	 * Render recently viewed products
	 */
	function renderRecentlyViewed() {
		const container = sidebar.querySelector(SELECTORS.recentlyViewed);
		if (!container) return;

		let items = [];
		try {
			items =
				JSON.parse(localStorage.getItem(STORAGE_KEYS.recentlyViewed)) || [];
		} catch (e) {
			items = [];
		}

		if (items.length === 0) {
			container.innerHTML = `
        <div class="member-sidebar__recent-empty">
          No recently viewed products
        </div>
      `;
			return;
		}

		// Show max 6 items
		const displayItems = items.slice(0, 6);

		container.innerHTML = displayItems
			.map(
				(item) => `
      <a href="${item.url || "#"}" class="member-sidebar__recent-item">
        <img 
          src="${item.image || "https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder.png"}" 
          alt="${escapeHtml(item.title || "Product")}"
          loading="lazy"
        >
        <span class="member-sidebar__recent-title">${escapeHtml(item.title || "Product")}</span>
      </a>
    `,
			)
			.join("");
	}

	/**
	 * Persist garage changes through the shared garage helper when available.
	 * Guests and fallback paths stay localStorage-only.
	 */
	function persistGarageVehicles(vehicles, location) {
		try {
			if (typeof window.setSearchTerms === "function") {
				window.setSearchTerms(vehicles, location);
				return;
			}

			localStorage.setItem(STORAGE_KEYS.searchTerms, JSON.stringify(vehicles));
			window.dispatchEvent(new Event("searchTermsUpdated"));
		} catch (error) {
			console.error("[MemberSidebar] Garage persist error:", error);
		}
	}

	/**
	 * Escape HTML to prevent XSS
	 */
	function escapeHtml(str) {
		if (!str) return "";
		const div = document.createElement("div");
		div.textContent = str;
		return div.innerHTML;
	}

	// Initialize when DOM is ready
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}

	// GLOBAL CLICK LISTENER - catches clicks even if init failed
	document.addEventListener("click", (e) => {
		const trigger =
			e.target &&
			e.target.closest &&
			e.target.closest("[data-open-member-sidebar]");
		if (trigger) {
			e.preventDefault();
			e.stopPropagation();
			if (!shouldOpenSidebar(e)) return;
			console.log("Global listener caught member-sidebar trigger click");
			if (
				window.MemberSidebar &&
				typeof window.MemberSidebar.open === "function"
			) {
				window.MemberSidebar.open(e);
			} else {
				// Fallback: try to open sidebar directly
				const sidebar = document.querySelector("[data-member-sidebar]");
				if (sidebar) {
					sidebar.classList.add("is-open");
					sidebar.setAttribute("aria-hidden", "false");
					document.body.classList.add("member-sidebar-open");
					console.log("Sidebar opened via fallback");
				} else {
					console.error(
						"MemberSidebar not found - sidebar element missing from DOM",
					);
				}
			}
		}
	});
})();
